/**
 * Product Card New - Gallery and shipping calculations
 * Maneja la galería de imágenes interactiva y cálculo de fechas de entrega
 */

class ProductCardGallery {
  constructor(container) {
    this.container = container;
    this.galleryContainer = container.querySelector('[data-product-gallery]');
    if (!this.galleryContainer) return;

    this.images = Array.from(this.galleryContainer.querySelectorAll('.product-card-new__gallery-image'));
    this.paginationDots = Array.from(this.galleryContainer.querySelectorAll('.product-card-new__pagination-dot'));
    this.currentIndex = 0;
    this.isHovering = false;
    this.imagesLoaded = false;

    this.init();
  }

  init() {
    // Solo inicializar si hay más de 1 imagen
    if (this.images.length <= 1) return;

    // Detectar si es dispositivo táctil
    this.isTouchDevice = ('ontouchstart' in window) || (navigator.maxTouchPoints > 0);

    if (!this.isTouchDevice) {
      // Desktop: hover y movimiento del mouse SOLO en dispositivos no táctiles
      this.galleryContainer.addEventListener('mouseenter', () => this.onMouseEnter());
      this.galleryContainer.addEventListener('mouseleave', () => this.onMouseLeave());
      this.galleryContainer.addEventListener('mousemove', (e) => this.onMouseMove(e));
    } else {
      // Mobile: touch/swipe - NO passive para poder usar preventDefault
      this.touchStartX = 0;
      this.touchEndX = 0;
      this.touchStartY = 0;
      this.touchEndY = 0;
      this.isSwiping = false;
      
      this.galleryContainer.addEventListener('touchstart', (e) => this.onTouchStart(e), { passive: false });
      this.galleryContainer.addEventListener('touchmove', (e) => this.onTouchMove(e), { passive: false });
      this.galleryContainer.addEventListener('touchend', (e) => this.onTouchEnd(e), { passive: false });
    }
    
    // Click en los puntos del paginador (ambos dispositivos)
    this.paginationDots.forEach((dot, index) => {
      dot.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.setActiveImage(index);
      });
    });
  }

  onTouchStart(e) {
    this.touchStartX = e.changedTouches[0].clientX;
    this.touchStartY = e.changedTouches[0].clientY;
    this.touchEndX = this.touchStartX;
    this.touchEndY = this.touchStartY;
    this.isSwiping = false;
    
    // Precargar imágenes en el primer touch
    if (!this.imagesLoaded) {
      this.preloadImages();
    }
  }

  onTouchMove(e) {
    this.touchEndX = e.changedTouches[0].clientX;
    this.touchEndY = e.changedTouches[0].clientY;
    
    if (!this.isSwiping) {
      // Calcular distancias
      const deltaX = Math.abs(this.touchEndX - this.touchStartX);
      const deltaY = Math.abs(this.touchEndY - this.touchStartY);
      
      // Detectar si el usuario está haciendo swipe horizontal en la galería
      if (deltaX > 10 || deltaY > 10) {
        if (deltaX > deltaY) {
          // Swipe horizontal detectado
          const swipeDirection = this.touchEndX - this.touchStartX;
          
          // Verificar si hay espacio para moverse en la galería
          const canGoNext = this.currentIndex < this.images.length - 1;
          const canGoPrev = this.currentIndex > 0;
          const isSwipeLeft = swipeDirection < 0; // Swipe izquierda
          const isSwipeRight = swipeDirection > 0; // Swipe derecha
          
          // Solo prevenir propagación si hay espacio para moverse en la galería
          if ((isSwipeLeft && canGoNext) || (isSwipeRight && canGoPrev)) {
            // Hay espacio en la galería - bloquear carrusel
            this.isSwiping = true;
            e.preventDefault();
            e.stopPropagation();
          }
          // Si no hay espacio, dejar que el swipe se propague al carrusel
        }
        // Si deltaY > deltaX, es scroll vertical - no hacer nada y dejar que se propague
      }
    } else {
      // Ya estamos en modo swipe de galería, seguir previniendo
      e.preventDefault();
      e.stopPropagation();
    }
  }

  onTouchEnd(e) {
    if (!this.isSwiping) return;
    
    const swipeDistanceX = this.touchEndX - this.touchStartX;
    const swipeDistanceY = Math.abs(this.touchEndY - this.touchStartY);
    
    // Asegurar que fue un swipe horizontal
    if (Math.abs(swipeDistanceX) > 30) {
      e.preventDefault();
      e.stopPropagation();
      
      // Swipe izquierda (negativo) = siguiente imagen
      if (swipeDistanceX < -30) {
        const nextIndex = Math.min(this.currentIndex + 1, this.images.length - 1);
        this.setActiveImage(nextIndex);
      } 
      // Swipe derecha (positivo) = imagen anterior
      else if (swipeDistanceX > 30) {
        const prevIndex = Math.max(this.currentIndex - 1, 0);
        this.setActiveImage(prevIndex);
      }
    }
    
    this.isSwiping = false;
  }

  onMouseEnter() {
    // Solo en desktop (no dispositivos táctiles)
    if (this.isTouchDevice) return;
    
    this.isHovering = true;
    
    // Precargar imágenes si no se han cargado
    if (!this.imagesLoaded) {
      this.preloadImages();
    }
  }

  onMouseLeave() {
    // Solo en desktop (no dispositivos táctiles)
    if (this.isTouchDevice) return;
    
    this.isHovering = false;
    
    // Volver a la primera imagen
    this.setActiveImage(0);
  }

  onMouseMove(e) {
    // Solo en desktop (no dispositivos táctiles)
    if (this.isTouchDevice || !this.isHovering || this.images.length <= 1) return;

    // Calcular el índice basado en la posición horizontal del cursor (DESKTOP)
    const rect = this.galleryContainer.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percentage = x / rect.width;
    const index = Math.floor(percentage * this.images.length);
    const clampedIndex = Math.max(0, Math.min(index, this.images.length - 1));

    if (clampedIndex !== this.currentIndex) {
      this.setActiveImage(clampedIndex);
    }
  }

  preloadImages() {
    this.images.forEach((img, index) => {
      if (index === 0) return; // La primera ya está cargada

      const src = img.getAttribute('data-src');
      const srcset = img.getAttribute('data-srcset');

      if (src) {
        img.src = src;
        img.removeAttribute('data-src');
      }

      if (srcset) {
        img.srcset = srcset;
        img.removeAttribute('data-srcset');
      }
    });

    this.imagesLoaded = true;
  }

  setActiveImage(index) {
    if (index === this.currentIndex) return;

    // Remover clase active de todas las imágenes
    this.images.forEach(img => {
      img.classList.remove('product-card-new__gallery-image--active');
      img.style.display = 'none';
    });

    // Activar la imagen seleccionada
    const targetImage = this.images[index];
    if (targetImage) {
      targetImage.style.display = 'block';
      // Pequeño delay para que la transición funcione
      setTimeout(() => {
        targetImage.classList.add('product-card-new__gallery-image--active');
      }, 10);
    }

    // Actualizar paginador
    this.paginationDots.forEach((dot, dotIndex) => {
      if (dotIndex === index) {
        dot.classList.add('is-active');
      } else {
        dot.classList.remove('is-active');
      }
    });

    this.currentIndex = index;
  }
}

class ProductCardShipping {
  constructor(container) {
    this.container = container;
    this.deliveryDateElement = container.querySelector('[data-delivery-date-value]');
    this.pickupDateElement = container.querySelector('[data-pickup-date-value]');

    if (this.deliveryDateElement || this.pickupDateElement) {
      this.calculateDates();
    }
  }

  calculateDates() {
    const deliveryConfig = {
      monday: window.Shopify?.theme?.settings?.product_card_delivery_monday !== false,
      tuesday: window.Shopify?.theme?.settings?.product_card_delivery_tuesday !== false,
      wednesday: window.Shopify?.theme?.settings?.product_card_delivery_wednesday !== false,
      thursday: window.Shopify?.theme?.settings?.product_card_delivery_thursday !== false,
      friday: window.Shopify?.theme?.settings?.product_card_delivery_friday !== false,
      saturday: window.Shopify?.theme?.settings?.product_card_delivery_saturday === true,
      sunday: window.Shopify?.theme?.settings?.product_card_delivery_sunday === true,
    };

    const pickupConfig = {
      monday: window.Shopify?.theme?.settings?.product_card_pickup_monday !== false,
      tuesday: window.Shopify?.theme?.settings?.product_card_pickup_tuesday !== false,
      wednesday: window.Shopify?.theme?.settings?.product_card_pickup_wednesday !== false,
      thursday: window.Shopify?.theme?.settings?.product_card_pickup_thursday !== false,
      friday: window.Shopify?.theme?.settings?.product_card_pickup_friday !== false,
      saturday: window.Shopify?.theme?.settings?.product_card_pickup_saturday === true,
      sunday: window.Shopify?.theme?.settings?.product_card_pickup_sunday === true,
    };

    const pickupDaysFrom = window.Shopify?.theme?.settings?.product_card_pickup_days_from || 1;
    const specificDates = window.Shopify?.theme?.settings?.product_card_delivery_specific_dates || '';

    // Calcular fecha de entrega
    if (this.deliveryDateElement) {
      const deliveryDate = this.getNextAvailableDate(deliveryConfig, 1, specificDates);
      this.deliveryDateElement.textContent = this.formatDate(deliveryDate);
    }

    // Calcular fecha de retiro
    if (this.pickupDateElement) {
      const pickupDate = this.getNextAvailableDate(pickupConfig, pickupDaysFrom);
      this.pickupDateElement.textContent = this.formatDateRelative(pickupDate);
    }
  }

  getNextAvailableDate(config, daysFromNow = 1, excludeDates = '') {
    const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const excludedDates = excludeDates.split(',').map(d => d.trim()).filter(Boolean);
    
    let date = new Date();
    date.setDate(date.getDate() + daysFromNow);

    let attempts = 0;
    const maxAttempts = 30; // Evitar loop infinito

    while (attempts < maxAttempts) {
      const dayName = dayNames[date.getDay()];
      const formattedDate = this.formatDateDDMM(date);
      
      // Verificar si este día está habilitado y no está en la lista de exclusión
      if (config[dayName] && !excludedDates.includes(formattedDate)) {
        return date;
      }

      date.setDate(date.getDate() + 1);
      attempts++;
    }

    return date; // Retornar la fecha calculada aunque no cumpla condiciones
  }

  formatDate(date) {
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    return `${day}/${month}`;
  }

  formatDateDDMM(date) {
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    return `${day}/${month}`;
  }

  formatDateRelative(date) {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    
    const targetDate = new Date(date);
    targetDate.setHours(0, 0, 0, 0);

    if (targetDate.getTime() === today.getTime()) {
      return 'hoy';
    } else if (targetDate.getTime() === tomorrow.getTime()) {
      return 'mañana';
    } else {
      return this.formatDate(date);
    }
  }
}

class ProductCardWishlist {
  constructor(container) {
    this.container = container;
    this.button = container.querySelector('[data-wishlist-toggle]');
    
    if (this.button) {
      this.button.addEventListener('click', (e) => this.toggle(e));
    }
  }

  toggle(e) {
    e.preventDefault();
    e.stopPropagation();

    // Toggle visual state
    if (this.button) {
      this.button.classList.toggle('is-active');
    }

    // TODO: Implementar lógica de wishlist con app/backend
    console.log('Wishlist toggled (TODO: implementar funcionalidad)');
  }
}

class ProductCardInfoHeight {
  static normalizeHeights() {
    // Buscar todas las grillas con product cards
    const grids = document.querySelectorAll('.product-grid-slider, .main-collection-product-grid, .main-search, .featured-collection, .related-products');
    
    grids.forEach(grid => {
      // Obtener todos los product cards de esta grilla específica
      const cards = grid.querySelectorAll('.product-card-new__info');
      if (cards.length === 0) return;

      // Calcular la altura máxima de los contenedores info en esta grilla
      let maxHeight = 0;
      cards.forEach(infoElement => {
        // Resetear min-height para obtener altura natural
        infoElement.style.minHeight = '';
        const height = infoElement.offsetHeight;
        if (height > maxHeight) {
          maxHeight = height;
        }
      });

      // Aplicar el min-height a todos los cards de esta grilla
      if (maxHeight > 0) {
        cards.forEach(infoElement => {
          infoElement.style.minHeight = `${maxHeight}px`;
        });
      }
    });
  }
}

// Inicializar todas las tarjetas de producto
document.addEventListener('DOMContentLoaded', () => {
  const productCards = document.querySelectorAll('.product-card-new');
  
  productCards.forEach(card => {
    new ProductCardGallery(card);
    new ProductCardShipping(card);
    new ProductCardWishlist(card);
  });

  // Normalizar alturas por grilla después de que todo esté renderizado
  setTimeout(() => {
    ProductCardInfoHeight.normalizeHeights();
  }, 100);
});

// También inicializar cuando se carguen dinámicamente (ej: filtros, infinite scroll)
if (window.MutationObserver) {
  let debounceTimer;
  
  const observer = new MutationObserver((mutations) => {
    let shouldRecalculate = false;
    
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType === 1) { // Element node
          if (node.classList && node.classList.contains('product-card-new')) {
            new ProductCardGallery(node);
            new ProductCardShipping(node);
            new ProductCardWishlist(node);
            shouldRecalculate = true;
          } else {
            // Buscar dentro del nodo añadido
            const cards = node.querySelectorAll && node.querySelectorAll('.product-card-new');
            if (cards && cards.length > 0) {
              cards.forEach(card => {
                new ProductCardGallery(card);
                new ProductCardShipping(card);
                new ProductCardWishlist(card);
              });
              shouldRecalculate = true;
            }
          }
        }
      });
    });

    // Recalcular alturas con debounce
    if (shouldRecalculate) {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        ProductCardInfoHeight.normalizeHeights();
      }, 200);
    }
  });

  observer.observe(document.body, {
    childList: true,
    subtree: true
  });
}

// Recalcular en resize (con debounce)
let resizeTimer;
window.addEventListener('resize', () => {
  clearTimeout(resizeTimer);
  resizeTimer = setTimeout(() => {
    ProductCardInfoHeight.normalizeHeights();
  }, 300);
});

// Exponer clases para uso externo si es necesario
window.ProductCardGallery = ProductCardGallery;
window.ProductCardShipping = ProductCardShipping;
window.ProductCardWishlist = ProductCardWishlist;
window.ProductCardInfoHeight = ProductCardInfoHeight;

